import {isStoppageEventType} from '@domain/shifthandover'
import {filterSxStyles} from '@hconnect/common/styles'
import {EventType, StoppageKind} from '@hconnect/common/types'
import {TextField, MenuItem, Checkbox, ListItemText, Box, SxProps, Theme} from '@mui/material'
import {RemoveFilterButton, SelectAllFilterButton} from '@widgets/common-filters'
import {EventTypeLabel} from '@widgets/common-shift-event'
import {isEmpty} from 'lodash'
import React from 'react'
import {useTranslation} from 'react-i18next'

import {SHIFT_EVENT_FILTERS_WIDGET_NAMESPACE} from '../consts'
import {
  EventTypeFilterValue,
  everyStoppageCategoryChecked,
  preprocessValueList,
  stoppageCategoryChecked
} from '../utils/eventTypeFilterUtils'

interface EventTypeFilterDropDown {
  onChange: (newValue: EventType[] | undefined) => void
  value?: EventType[]
  label: string
  name: string
  removeFilter?: (filter: string) => void
  'data-test-id'?: string
  isCollapsible?: boolean
  selectAll?: (options: EventType[]) => void
  options?: EventTypeFilterValue[]
  sx?: SxProps<Theme>
}

export const eventAndStoppageCodeTypes: EventTypeFilterValue[] = [
  'stoppage',
  StoppageKind.Incident,
  StoppageKind.Circumstance,
  StoppageKind.PlannedStoppage,
  'malfunction',
  'healthAndSafety',
  'task',
  'information',
  'idea',
  'environment',
  'parameterChange',
  'systemChangeRequest',
  'quality',
  'spot'
]

export function isStoppageKind(value: EventTypeFilterValue): value is StoppageKind {
  return Object.values(StoppageKind).includes(value as StoppageKind)
}

export const EventTypeFilterDropDown: React.FC<EventTypeFilterDropDown> = ({
  value = [],
  removeFilter,
  name,
  onChange,
  isCollapsible = false,
  'data-test-id': dataTestId,
  selectAll,
  options = eventAndStoppageCodeTypes,
  sx = filterSxStyles.textInputField,
  label
}) => {
  const {t} = useTranslation(SHIFT_EVENT_FILTERS_WIDGET_NAMESPACE)
  const isEachStoppageKind = options.every(isStoppageKind)

  return (
    <Box display="flex">
      <TextField
        variant={'filled'}
        label={label}
        sx={sx}
        data-test-id={dataTestId}
        value={value}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          const val: EventTypeFilterValue[] =
            (event.target.value as unknown as EventTypeFilterValue[] | undefined) ?? []
          const eventTypeList = preprocessValueList(val)
          onChange(isEmpty(eventTypeList) ? undefined : eventTypeList)
        }}
        SelectProps={{
          multiple: true,
          renderValue: (args: unknown) =>
            (args as EventTypeFilterValue[])
              .map((arg: EventTypeFilterValue) => t(`shiftEvent.eventType.${arg}`))
              .join(', ')
        }}
        select
      >
        {options.map((option) => (
          <MenuItem key={option} value={option} data-test-id={`${dataTestId}-${option}`}>
            <Checkbox
              sx={[
                {
                  pl: 0,
                  py: 0,
                  '&:hover': {
                    backgroundColor: 'transparent'
                  }
                },
                !isEachStoppageKind && isStoppageEventType(option) && {ml: 4}
              ]}
              checked={
                option === 'stoppage' ? everyStoppageCategoryChecked(value) : value.includes(option)
              }
              indeterminate={
                option === 'stoppage' &&
                stoppageCategoryChecked(value) &&
                !everyStoppageCategoryChecked(value)
              }
            />
            {isStoppageEventType(option) ? (
              <ListItemText>{t(`shiftEvent.eventType.${option}`)}</ListItemText>
            ) : (
              <EventTypeLabel eventType={option} />
            )}
          </MenuItem>
        ))}
        {isCollapsible ? null : (
          <>
            {selectAll && (
              <SelectAllFilterButton
                keyName={name}
                onClick={() => {
                  const allValues = options.filter(
                    (v) => v !== 'stoppage'
                  ) as unknown as EventTypeFilterValue[]
                  const processed = preprocessValueList(allValues)
                  selectAll(processed)
                }}
              />
            )}
            <RemoveFilterButton keyName={name} removeFilter={removeFilter} />
          </>
        )}
      </TextField>
    </Box>
  )
}
